import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, useGoogleOneTapLogin } from '@react-oauth/google';
import API_BASE_URL from '../api.js';

export const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch { return false; }
};

/* ─── Reusable auth card ─────────────────────────────────────────────────── */
export const AuthCard = ({ onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const btnContainerRef       = useRef(null);
  const [btnWidth, setBtnWidth] = useState(280);

  useEffect(() => {
    if (!btnContainerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setBtnWidth(Math.floor(entry.contentRect.width));
    });
    observer.observe(btnContainerRef.current);
    return () => observer.disconnect();
  }, []);

  const handleGoogleSuccess = async (response) => {
    setError('');
    setLoading(true);
    try {
      const pendingRefCode = localStorage.getItem('pending_ref') || undefined;
      const res  = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ credential: response.credential, pendingRefCode }),
      });
      const data = await res.json();
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('auth_name',  data.name);
        localStorage.setItem('auth_email', data.email);
        localStorage.removeItem('pending_ref');
        onClose?.();
        navigate('/home');
      } else {
        setError(data.error || 'Google sign-in failed. Please try again.');
      }
    } catch (err) {
      console.error('[auth] fetch error:', err);
      setError('Cannot reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: 380,
      boxSizing: 'border-box',
      background: '#ffffff',
      borderRadius: 18,
      border: '1px solid #eaecf0',
      boxShadow: '0 4px 24px rgba(15,23,42,0.07)',
      padding: 'clamp(20px, 5vw, 40px) clamp(16px, 5vw, 36px)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 'clamp(12px, 3vw, 20px)',
      position: 'relative',
    }}>

      {/* Close button — only shown when used as modal */}
      {onClose && (
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 12, right: 12,
            background: '#f1f5f9', border: 'none', borderRadius: '50%',
            width: 30, height: 30, display: 'flex', alignItems: 'center',
            justifyContent: 'center', cursor: 'pointer',
            fontSize: '1rem', color: '#64748b', fontWeight: 700,
            flexShrink: 0,
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
          onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}
        >×</button>
      )}

      {/* Logo + heading */}
      <div style={{ textAlign: 'center', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
          <img src="/logo192.png" alt="AssessMonk" style={{ width: 28, height: 28, borderRadius: 8, objectFit: 'contain' }} />
          <span style={{ fontWeight: 800, fontSize: 'clamp(0.85rem, 3vw, 1rem)', letterSpacing: '-0.02em', color: '#0f172a' }}>AssessMonk</span>
        </div>
        <h2 style={{ margin: '0 0 4px', fontSize: 'clamp(1rem, 3.5vw, 1.1rem)', fontWeight: 800, letterSpacing: '-0.02em', color: '#0f172a' }}>
          Welcome
        </h2>
        <p style={{ margin: 0, fontSize: 'clamp(0.72rem, 2.5vw, 0.8rem)', color: '#94a3b8', lineHeight: 1.5 }}>
          Sign in to your clinical assessment workspace
        </p>
      </div>

      {/* Error banner */}
      {error && (
        <div style={{ width: '100%', boxSizing: 'border-box', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', fontSize: '0.82rem', color: '#991b1b', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <span style={{ flexShrink: 0 }}>⚠</span>
          <span>{error}</span>
        </div>
      )}

      {/* Google sign-in — full-width container so button fills available space */}
      <div ref={btnContainerRef} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        {loading ? (
          <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600, padding: '10px 0' }}>Signing in…</div>
        ) : (
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError('Google sign-in failed. Please try again.')}
            width={String(btnWidth)}
          />
        )}
      </div>

      <p style={{ margin: 0, fontSize: 'clamp(0.68rem, 2vw, 0.72rem)', color: '#cbd5e1', textAlign: 'center', lineHeight: 1.6 }}>
        By signing in you agree to our{' '}
        <a href="/terms" style={{ color: '#94a3b8', textDecoration: 'underline' }}>Terms</a>
        {' '}and{' '}
        <a href="/privacy" style={{ color: '#94a3b8', textDecoration: 'underline' }}>Privacy Policy</a>
      </p>

    </div>
  );
};

/* ─── Full-page login (keeps /app route working) ─────────────────────────── */
const Login = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (isTokenValid(localStorage.getItem('auth_token'))) navigate('/home', { replace: true });
  }, [navigate]);

  useGoogleOneTapLogin({
    onSuccess: async (credentialResponse) => {
      try {
        const pendingRefCode = localStorage.getItem('pending_ref') || undefined;
        const res  = await fetch(`${API_BASE_URL}/api/auth/google`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ credential: credentialResponse.credential, pendingRefCode }),
        });
        const data = await res.json();
        if (data.token) {
          localStorage.setItem('auth_token', data.token);
          localStorage.setItem('auth_name',  data.name);
          localStorage.setItem('auth_email', data.email);
          localStorage.removeItem('pending_ref');
          navigate('/home');
        }
      } catch { /* silent — button fallback visible */ }
    },
    onError:               () => {},
    disabled:              isTokenValid(localStorage.getItem('auth_token')),
    auto_select:           false,
    cancel_on_tap_outside: true,
    itp_support:           true,
  });

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f6f8fb',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      color: '#0f172a',
    }}>
      <AuthCard />
      <p style={{ marginTop: 20, fontSize: '0.72rem', color: '#cbd5e1', textAlign: 'center' }}>
        Clinical assessment tool for physiotherapists
      </p>
    </div>
  );
};

export default Login;
